import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Spinner } from "src/core/components/custom-ui/spinner";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/core/components/ui/select";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { RISK_LEVEL_CLASSES } from "src/core/constants/risk";
import { cn } from "src/core/lib/utils";

const isRiskOption = (option: ISelectOption | IRiskOption): option is IRiskOption => {
  return "risk" in option;
};

type FormSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  options: ISelectOption[] | IRiskOption[];
  placeholder?: string;
  required?: boolean;
  softRequired?: boolean;
  className?: string;
  control?: Control<T>;
  isLoading?: boolean;
  clearable?: boolean;
  withBadge?: boolean;
  disabled?: boolean;
  wrapperClassName?: string;
};

const NONE_VALUE = "none";

/**
 *
 * @param softRequired - (boolean) Permet de gérer un affichage de type "warning" (orange)
 * pour les champs qui sont nécessaires à la validation d'un dossier.
 */
export function FormSelect<T extends FieldValues>({
  name,
  label = "",
  options,
  placeholder,
  required = false,
  softRequired = false,
  className = "w-[360px]",
  control,
  isLoading = false,
  clearable = false,
  withBadge = false,
  disabled = false,
  wrapperClassName = "",
}: FormSelectProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedOption = options.find((option) => option.value === field.value);
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        return (
          <FormItem className={wrapperClassName}>
            {label !== "" && (
              <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
                {label}
              </FormLabel>
            )}
            {isLoading ? (
              <div className={`h-[40px] flex items-center ${className}`}>
                <Spinner />
              </div>
            ) : (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isLoading || disabled}
                >
                  <FormControl>
                    <SelectTrigger
                      className={cn(
                        className,
                        "select-wrapper",
                        isInvalid && "border-alert ring-alert",
                        showSoftRequired && "border-2 border-dashed border-orange-medium"
                      )}
                      data-testid={`select-wrapper-${name}`}
                    >
                      <SelectValue
                        placeholder={
                          placeholder ? t(placeholder) : t("global.form.select.placeholder")
                        }
                      >
                        {selectedOption && (
                          <div className="flex items-center gap-2 text-ellipsis overflow-hidden whitespace-nowrap">
                            {withBadge && selectedOption && isRiskOption(selectedOption) && (
                              <div
                                className={cn(
                                  "min-w-[16px] h-[16px] rounded-full",
                                  RISK_LEVEL_CLASSES[selectedOption.risk]
                                )}
                              />
                            )}
                            <span className={cn("truncate", disabled ? "opacity-50" : "")}>
                              {selectedOption.label}
                            </span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {clearable && (
                      <SelectItem value={NONE_VALUE} className="text-primary/50">
                        {t("global.form.select.none")}
                      </SelectItem>
                    )}
                    {options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {withBadge && isRiskOption(option) ? (
                          <div className="flex items-center gap-2 text-ellipsis overflow-hidden whitespace-nowrap">
                            {option.risk && (
                              <div
                                className={`w-[16px] h-[16px] rounded-full ${
                                  RISK_LEVEL_CLASSES[option.risk]
                                }`}
                              />
                            )}
                            <span className="font-light truncate">{option.label}</span>
                          </div>
                        ) : (
                          option.label
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                {showSoftRequired && (
                  <p className="font-light text-sm text-muted">
                    {t("global.form.warning.softRequired")}
                  </p>
                )}
              </>
            )}
          </FormItem>
        );
      }}
    />
  );
}
